// WHAT: Half Circle Progress Component
// WHY:  Displays progress as a half-circle arc (0-100%) for Oura score visualization
// DOMAIN: Health → Oura
// DEPENDENCIES: React, SVG path calculations
// RISKS:
//   - NOTE: Percent value is clamped to 0-100 range
//   - NOTE: SVG arc calculation uses polar-to-cartesian conversion
//   - NOTE: Animation uses CSS transition (may not work in all browsers)

interface HalfCircleProgressProperties {
  bgColor?: string;
  color?: string;
  percent: number; // 0–100+
  size?: number;
  strokeWidth?: number;
}

/**
 * WHAT:
 *   Half-circle progress indicator component using SVG arc.
 *
 * WHY:
 *   Provides visual progress representation for Oura scores (readiness, sleep, activity)
 *   in a compact half-circle format.
 *
 * PROPS:
 *   - percent: number — Progress percentage (0-100+, clamped to 0-100)
 *   - strokeWidth?: number — Stroke width in pixels (default: 10)
 *   - size?: number — SVG size in pixels (default: 180)
 *   - color?: string — Progress arc color (default: "#4ade80" - green)
 *   - bgColor?: string — Background arc color (default: "#fff" - white)
 *
 * BEHAVIOR:
 *   - Renders half-circle arc from 180° (9 o'clock) to 0° (3 o'clock)
 *   - Clamps percent value to 0-100 range
 *   - Calculates arc length and dash offset for progress animation
 *   - Uses stroke-dasharray and stroke-dashoffset for progress effect
 *   - Applies CSS transition for smooth animation (0.6s cubic-bezier)
 *   - Background arc shown with 20% opacity
 *
 * RISKS:
 *   - NOTE: Percent values > 100 are clamped to 100
 *   - NOTE: Arc calculation assumes standard SVG coordinate system
 *   - NOTE: CSS transition may not animate smoothly on all browsers
 *
 * @param root0
 *
 * @param root0.percent
 *
 * @param root0.strokeWidth
 *
 * @param root0.size
 *
 * @param root0.color
 *
 * @param root0.bgColor
 *
 * @returns JSX element displaying half-circle progress indicator.
 *
 */
export const HalfCircleProgress = ({
  bgColor = "#fff",
  color = "#4ade80",
  percent,
  size = 180,
  strokeWidth = 10,
}: HalfCircleProgressProperties) => {
  const radius = (size - strokeWidth) / 2;
  const arcLength = Math.PI * radius;
  const p = Math.max(0, Math.min(percent, 100));
  const dashoffset = arcLength * (1 - p / 100);

  return (
    <svg className="block" height={size / 2} width={size}>
      {/* Hintergrund */}
      <path
        d={describeArc(size / 2, size / 2, radius, 180, 0)}
        fill="none"
        opacity={0.2}
        stroke={bgColor}
        strokeWidth={strokeWidth}
      />
      {/* Progress */}
      <path
        d={describeArc(size / 2, size / 2, radius, 180, 0)}
        fill="none"
        stroke={color}
        strokeDasharray={arcLength}
        strokeDashoffset={dashoffset}
        strokeLinecap="round"
        strokeWidth={strokeWidth}
        style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(.4,2,.3,1)" }}
      />
    </svg>
  );
};

/**
 * WHAT:
 *   Converts polar coordinates (angle in degrees) to Cartesian coordinates (x, y).
 *
 * WHY:
 *   Helper function for SVG arc path calculations.
 *
 * @param centerX - Center X coordinate
 *
 * @param centerY - Center Y coordinate
 *
 * @param radius - Radius of the circle
 *
 * @param angleInDegrees - Angle in degrees (0° = 3 o'clock, 180° = 9 o'clock)
 *
 * @returns Object with x and y coordinates.
 *
 */
function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) {
  const angleInRadians = (angleInDegrees * Math.PI) / 180; // KEINE -90° Verschiebung!
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

/**
 * WHAT:
 *   Generates SVG path string for an arc from start angle to end angle.
 *
 * WHY:
 *   Creates the path data for half-circle progress visualization.
 *
 * @param cx - Center X coordinate
 *
 * @param cy - Center Y coordinate
 *
 * @param r - Radius of the arc
 *
 * @param startAngle - Start angle in degrees (180° = 9 o'clock)
 *
 * @param endAngle - End angle in degrees (0° = 3 o'clock)
 *
 * @returns SVG path string (e.g., "M 100 50 A 50 50 0 0 1 200 50").
 *
 */
function describeArc(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
): string {
  const start = polarToCartesian(cx, cy, r, startAngle); // 180° = 9 Uhr
  const end = polarToCartesian(cx, cy, r, endAngle); // 0° = 3 Uhr
  const largeArcFlag = "0";
  const sweepFlag = "1"; // gegen Uhrzeigersinn für oben herum

  return ["M", start.x, start.y, "A", r, r, 0, largeArcFlag, sweepFlag, end.x, end.y].join(" ");
}
